import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full px-6 py-8" }
const _hoisted_2 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 p-8" }
const _hoisted_3 = { class: "flex items-center justify-between mb-6" }
const _hoisted_4 = { class: "text-sm text-gray-500" }
const _hoisted_5 = {
  key: 0,
  class: "text-center py-12"
}
const _hoisted_6 = {
  key: 1,
  class: "text-center py-12"
}
const _hoisted_7 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "aspect-video bg-gray-200 flex items-center justify-center relative" }
const _hoisted_10 = {
  key: 0,
  class: "text-center"
}
const _hoisted_11 = ["src", "alt"]
const _hoisted_12 = { class: "absolute top-2 right-2" }
const _hoisted_13 = { class: "p-4" }
const _hoisted_14 = { class: "text-lg font-semibold text-gray-900 mb-2 line-clamp-2" }
const _hoisted_15 = { class: "text-sm text-gray-600 mb-3" }
const _hoisted_16 = { class: "flex flex-wrap gap-1 mb-4" }
const _hoisted_17 = {
  key: 0,
  class: "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
}
const _hoisted_18 = { class: "text-xs text-gray-500 mb-4" }
const _hoisted_19 = { class: "flex items-center gap-2" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = ["onClick", "title"]
const _hoisted_22 = { class: "flex items-center gap-2 mt-2" }
const _hoisted_23 = ["onClick"]
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "flex items-center justify-between mb-6" }
const _hoisted_26 = { class: "text-2xl font-bold text-gray-900" }
const _hoisted_27 = { class: "text-center py-4" }
const _hoisted_28 = { class: "text-sm text-gray-500 mb-4" }
const _hoisted_29 = { class: "flex justify-end gap-3" }

import { ref, onMounted } from 'vue'
import { PageHeader, LookbookForm, ModalComp } from '@/components'
import { getLookbooksByVendeur, getLookbookCategories } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'
import type { LookbookDisplay, LookbookFormData } from '@/types/lookbook'
import {useRouter} from "vue-router";

// Stores

export default /*@__PURE__*/_defineComponent({
  __name: 'LookbookManagementView',
  setup(__props) {

const authStore = useAuthStore()
const router = useRouter()
// Notifications
const { success, error } = useNotification()

// État global
const lookbooks = ref<LookbookDisplay[]>([])
const showForm = ref(false)
const editingLookbook = ref<LookbookDisplay | null>(null)
const isLoading = ref(false)
const formSection = ref<HTMLDivElement>()

// État de la modal de suppression
const showDeleteModal = ref(false)
const lookbookToDelete = ref<LookbookDisplay | null>(null)

// Méthodes
const loadLookbooks = async () => {
  try {
    isLoading.value = true
    const idVendeur = authStore.user?.id_vendeur || authStore.user?.id
    
    if (!idVendeur) {
      error('ID vendeur introuvable')
      return
    }
    
    const lookbooksData = await getLookbooksByVendeur(idVendeur)
    
    // Charger les catégories pour chaque lookbook
    const lookbooksWithCategories = await Promise.all(
      lookbooksData.map(async (lookbook) => {
        try {
          const categories = await getLookbookCategories(lookbook.id)
          return {
            ...lookbook,
            categories: categories.map(cat => cat.id_categorie)
          }
        } catch (err) {
          return lookbook
        }
      })
    )
    
    lookbooks.value = lookbooksWithCategories
  } catch (err) {
    error('Erreur lors du chargement des lookbooks')
    lookbooks.value = []
  } finally {
    isLoading.value = false
  }
}

const showFormSection = () => {
  editingLookbook.value = null // Réinitialiser pour avoir un formulaire vierge
  showForm.value = true
  // Scroll vers le formulaire
  setTimeout(() => {
    formSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, 100)
}

const hideForm = () => {
  showForm.value = false
  editingLookbook.value = null
}

const handleEdit = (lookbook: LookbookDisplay) => {
  editingLookbook.value = lookbook
  // Scroll vers le point d'ancrage (fin de la liste des lookbooks)
  setTimeout(() => {
    const scrollAnchor = document.getElementById('scroll-anchor')
    if (scrollAnchor) {
      scrollAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      // Fallback vers le formulaire si le point d'ancrage n'est pas trouvé
      formSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, 100)
}

const handleToggleActive = async (lookbook: LookbookDisplay) => {
  try {
    const { toggleLookbookActif } = await import('@/api')
    await toggleLookbookActif(lookbook.id)
    success(lookbook.actif ? 'Lookbook désactivé avec succès' : 'Lookbook activé avec succès')
    // Recharger les lookbooks pour obtenir l'état à jour
    await loadLookbooks()
  } catch (err) {
    error('Erreur lors de la modification du statut')
  }
}

const handleDelete = (lookbook: LookbookDisplay) => {
  lookbookToDelete.value = lookbook
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (!lookbookToDelete.value) return
  
  try {
    const { deleteLookbook } = await import('@/api')
    await deleteLookbook(lookbookToDelete.value.id)
    success('Lookbook supprimé avec succès')
    showDeleteModal.value = false
    lookbookToDelete.value = null
    await loadLookbooks()
  } catch (err) {
    error('Erreur lors de la suppression du lookbook')
  }
}

const handlePreview = (lookbook: LookbookDisplay) => {
  const url = `https://www.efashion-paris.com/vendeurs-lookbook/&idModLookbook=${lookbook.id}`
  window.open(url, '_blank')
}

const handleSubmitForm = async (formData: LookbookFormData) => {
  try {
    const idVendeur = authStore.user?.id_vendeur || authStore.user?.id
    
    if (!idVendeur) {
      error('ID vendeur introuvable')
      return
    }
    
    if (editingLookbook.value) {
      // Modification d'un lookbook existant
      const { updateLookbook, uploadLookbookBanniere, saveLookbookCategories } = await import('@/api')
      
      await updateLookbook({
        id_mod_lookbook: editingLookbook.value.id,
        annee: formData.annee,
        id_collection: formData.collection,
        data: JSON.stringify({
          couleur_cadre: formData.couleurCadre,
          couleur_texte: formData.couleurTexte,
        }),
        titre_fr: formData.titre.francais,
        titre_en: formData.titre.anglais,
        titre_es: formData.titre.espagnol,
        titre_it: formData.titre.italien,
        description_fr: formData.description.francais,
        description_en: formData.description.anglais,
        description_es: formData.description.espagnol,
        description_it: formData.description.italien,
      })
      
      // Upload de la bannière si présente
      if (formData.banniere) {
        await uploadLookbookBanniere(editingLookbook.value.id, formData.banniere)
      }
      
      // Sauvegarder les catégories
      if (formData.categories && formData.categories.length > 0) {
        await saveLookbookCategories(editingLookbook.value.id, formData.categories)
      }
      
      success('Lookbook modifié avec succès')
    } else {
      // Création d'un nouveau lookbook
      const { createLookbook, uploadLookbookBanniere, saveLookbookCategories } = await import('@/api')
      
      const newLookbook = await createLookbook({
        id_vendeur: idVendeur,
        type: 0, // 0 = lookbook vendeur, 1 = edito Efashion
        annee: formData.annee,
        id_collection: formData.collection,
        data: JSON.stringify({
          couleur_cadre: formData.couleurCadre,
          couleur_texte: formData.couleurTexte,
        }),
        titre_fr: formData.titre.francais,
        titre_en: formData.titre.anglais,
        titre_es: formData.titre.espagnol,
        titre_it: formData.titre.italien,
        description_fr: formData.description.francais,
        description_en: formData.description.anglais,
        description_es: formData.description.espagnol,
        description_it: formData.description.italien,
        actif: false,
      })
      
      // Upload de la bannière si présente
      if (formData.banniere && newLookbook.id) {
        await uploadLookbookBanniere(newLookbook.id, formData.banniere)
      }
      
      // Sauvegarder les catégories
      if (formData.categories && formData.categories.length > 0 && newLookbook.id) {
        await saveLookbookCategories(newLookbook.id, formData.categories)
      }
      
      success('Lookbook créé avec succès')
    }
    
    // Recharger les lookbooks
    await loadLookbooks()
    
    // Fermer le formulaire et réinitialiser
    showForm.value = false
    editingLookbook.value = null
  } catch (err) {
    error('Erreur lors de la sauvegarde du lookbook')
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadLookbooks()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Liste des Lookbooks",
      subtitle: "Gérez vos lookbooks et collections",
      class: "mb-6 pr-10"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", { class: "flex justify-start mb-6" }, [
        _createElementVNode("button", {
          onClick: showFormSection,
          class: "inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-sm"
        }, _cache[2] || (_cache[2] = [
          _createElementVNode("i", { class: "fas fa-plus mr-2" }, null, -1),
          _createTextVNode(" Ajouter un lookbook ")
        ]))
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _cache[3] || (_cache[3] = _createElementVNode("h2", { class: "text-2xl font-bold text-gray-900" }, "Liste des Lookbooks", -1)),
          _createElementVNode("div", _hoisted_4, _toDisplayString(lookbooks.value.length) + " lookbook" + _toDisplayString(lookbooks.value.length > 1 ? 's' : ''), 1)
        ]),
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[4] || (_cache[4] = [
              _createElementVNode("i", { class: "fas fa-spinner fa-spin text-gray-400 text-4xl mb-4" }, null, -1),
              _createElementVNode("div", { class: "text-gray-600" }, "Chargement des lookbooks...", -1)
            ])))
          : (lookbooks.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[5] || (_cache[5] = [
                _createElementVNode("div", { class: "bg-blue-50 border border-blue-200 rounded-lg p-6 inline-block" }, [
                  _createElementVNode("i", { class: "fas fa-book-open text-blue-400 text-4xl mb-4" }),
                  _createElementVNode("div", { class: "text-blue-800 font-medium mb-2" }, "Aucun lookbook"),
                  _createElementVNode("div", { class: "text-blue-600 text-sm" }, " Vous n'avez aucun lookbook sur Efashion Paris. Créez votre premier lookbook ! ")
                ], -1)
              ])))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lookbooks.value, (lookbook) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: lookbook.id,
                    onClick: ($event: any) => (handleEdit(lookbook)),
                    class: "bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer"
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      (!lookbook.banniere)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[6] || (_cache[6] = [
                            _createElementVNode("i", { class: "fas fa-image text-gray-400 text-3xl mb-2" }, null, -1),
                            _createElementVNode("div", { class: "text-gray-500 text-sm" }, "Aucune couverture", -1)
                          ])))
                        : (_openBlock(), _createElementBlock("img", {
                            key: 1,
                            src: lookbook.banniere,
                            alt: lookbook.titre.francais,
                            class: "w-full h-full object-cover"
                          }, null, 8, _hoisted_11)),
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("span", {
                          class: _normalizeClass([
                  'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                  lookbook.actif 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                ])
                        }, [
                          _createElementVNode("i", {
                            class: _normalizeClass([lookbook.actif ? 'fas fa-check-circle' : 'fas fa-pause-circle', "mr-1"])
                          }, null, 2),
                          _createTextVNode(" " + _toDisplayString(lookbook.actif ? 'Actif' : 'Inactif'), 1)
                        ], 2)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("h3", _hoisted_14, _toDisplayString(lookbook.titre.francais), 1),
                      _createElementVNode("p", _hoisted_15, [
                        _cache[7] || (_cache[7] = _createElementVNode("i", { class: "fas fa-calendar mr-1" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(lookbook.annee) + " - Collection " + _toDisplayString(lookbook.collection), 1)
                      ]),
                      _createElementVNode("div", _hoisted_16, [
                        (lookbook.categories.length > 0)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_17, [
                              _cache[8] || (_cache[8] = _createElementVNode("i", { class: "fas fa-tag mr-1" }, null, -1)),
                              _createTextVNode(" " + _toDisplayString(lookbook.categories.length) + " catégorie" + _toDisplayString(lookbook.categories.length > 1 ? 's' : ''), 1)
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_18, [
                        _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fas fa-clock mr-1" }, null, -1)),
                        _createTextVNode(" Créé le " + _toDisplayString(formatDate(lookbook.dateCreation)), 1)
                      ]),
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (handleEdit(lookbook)), ["stop"]),
                          class: "flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors",
                          title: "Éditer"
                        }, _cache[10] || (_cache[10] = [
                          _createElementVNode("i", { class: "fas fa-edit mr-1" }, null, -1),
                          _createTextVNode(" Éditer ")
                        ]), 8, _hoisted_20),
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (handleToggleActive(lookbook)), ["stop"]),
                          class: _normalizeClass([
                  'flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md transition-colors',
                  lookbook.actif 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                ]),
                          title: lookbook.actif ? 'Désactiver' : 'Activer'
                        }, [
                          _createElementVNode("i", {
                            class: _normalizeClass([lookbook.actif ? 'fas fa-pause' : 'fas fa-play', "mr-1"])
                          }, null, 2),
                          _createTextVNode(" " + _toDisplayString(lookbook.actif ? 'Désactiver' : 'Activer'), 1)
                        ], 10, _hoisted_21)
                      ]),
                      _createElementVNode("div", _hoisted_22, [
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (handlePreview(lookbook)), ["stop"]),
                          class: "flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        }, _cache[11] || (_cache[11] = [
                          _createElementVNode("i", { class: "fas fa-eye mr-1" }, null, -1),
                          _createTextVNode(" Aperçu ")
                        ]), 8, _hoisted_23),
                        _createElementVNode("button", {
                          onClick: _withModifiers(($event: any) => (handleDelete(lookbook)), ["stop"]),
                          class: "inline-flex items-center justify-center w-10 h-10 border border-red-300 rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors",
                          title: "Supprimer"
                        }, _cache[12] || (_cache[12] = [
                          _createElementVNode("i", { class: "fas fa-trash text-sm" }, null, -1)
                        ]), 8, _hoisted_24)
                      ])
                    ])
                  ], 8, _hoisted_8))
                }), 128))
              ]))
      ]),
      _cache[20] || (_cache[20] = _createElementVNode("div", {
        id: "scroll-anchor",
        class: "h-0 -mt-20 pt-20"
      }, null, -1)),
      _withDirectives(_createElementVNode("div", {
        ref_key: "formSection",
        ref: formSection,
        class: "bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8"
      }, [
        _createElementVNode("div", _hoisted_25, [
          _createElementVNode("h2", _hoisted_26, _toDisplayString(editingLookbook.value ? 'Modifier le Lookbook' : 'Nouveau Lookbook'), 1),
          _createElementVNode("button", {
            onClick: hideForm,
            class: "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          }, _cache[13] || (_cache[13] = [
            _createElementVNode("i", { class: "fas fa-times mr-2" }, null, -1),
            _createTextVNode(" Fermer ")
          ]))
        ]),
        _createVNode(_unref(LookbookForm), {
          "initial-data": editingLookbook.value ? {
             id: editingLookbook.value.id,
             titre: editingLookbook.value.titre,
             description: editingLookbook.value.description,
             collection: editingLookbook.value.collection,
             annee: editingLookbook.value.annee,
             couleurCadre: editingLookbook.value.couleurCadre,
             couleurTexte: editingLookbook.value.couleurTexte,
             categories: editingLookbook.value.categories,
             banniere: editingLookbook.value.banniere
           } : undefined,
          onSubmit: handleSubmitForm
        }, null, 8, ["initial-data"])
      ], 512), [
        [_vShow, showForm.value || editingLookbook.value]
      ]),
      _createVNode(_unref(ModalComp), {
        isVisible: showDeleteModal.value,
        title: "Confirmer la suppression",
        size: "md",
        "onUpdate:isVisible": _cache[1] || (_cache[1] = ($event: any) => (showDeleteModal.value = false))
      }, {
        body: _withCtx(() => [
          _createElementVNode("div", _hoisted_27, [
            _cache[16] || (_cache[16] = _createElementVNode("div", { class: "mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4" }, [
              _createElementVNode("i", { class: "fas fa-exclamation-triangle text-red-600 text-xl" })
            ], -1)),
            _cache[17] || (_cache[17] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 mb-2" }, " Supprimer le lookbook ", -1)),
            _createElementVNode("p", _hoisted_28, [
              _cache[14] || (_cache[14] = _createTextVNode(" Êtes-vous sûr de vouloir supprimer le lookbook ")),
              _createElementVNode("strong", null, "\"" + _toDisplayString(lookbookToDelete.value?.titre.francais) + "\"", 1),
              _cache[15] || (_cache[15] = _createTextVNode(" ? "))
            ]),
            _cache[18] || (_cache[18] = _createElementVNode("p", { class: "text-xs text-gray-400" }, " Cette action est irréversible. ", -1))
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_29, [
            _createElementVNode("button", {
              onClick: _cache[0] || (_cache[0] = ($event: any) => (showDeleteModal.value = false)),
              type: "button",
              class: "px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
            }, " Annuler "),
            _createElementVNode("button", {
              onClick: confirmDelete,
              type: "button",
              class: "px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
            }, _cache[19] || (_cache[19] = [
              _createElementVNode("i", { class: "fas fa-trash mr-2" }, null, -1),
              _createTextVNode(" Supprimer ")
            ]))
          ])
        ]),
        _: 1
      }, 8, ["isVisible"])
    ])
  ], 64))
}
}

})